<template>
  <section
    ref="browserSection"
    class="section product-browser-section"
    :data-route-scroll-path="route.path"
    :data-route-scroll-navigation-id="routeScrollInstanceId"
    :data-route-scroll-ready="isRouteScrollReady ? 'true' : 'false'"
  >
    <div v-if="categories.length > 0" class="product-browser-tabbar">
      <div class="page-container page-container--narrow">
        <nav ref="categoryNav" class="product-tab-nav" aria-label="产品分类">
          <button
            type="button"
            class="product-tab-item"
            :class="{ 'is-active': !activeCategoryCode }"
            :aria-current="!activeCategoryCode ? 'page' : undefined"
            @click="changeCategory('')"
          >
            全部
          </button>
          <button
            v-for="category in categories"
            :key="category.id"
            type="button"
            class="product-tab-item"
            :class="{ 'is-active': category.code === activeCategoryCode }"
            :aria-current="category.code === activeCategoryCode ? 'page' : undefined"
            @click="changeCategory(category.code)"
          >
            {{ category.name }}
          </button>
          <span class="product-tab-indicator" aria-hidden="true"></span>
        </nav>
      </div>
    </div>

    <div class="page-container page-container--narrow">
      <div class="product-browser-content">
        <div v-if="showListLoading" class="product-state" role="status">
          <i class="i-lucide-loader-circle is-spinning" aria-hidden="true"></i>
          <span>正在加载产品...</span>
        </div>

        <div v-else-if="hasError" class="product-state is-error" role="alert">
          <i class="i-lucide-circle-alert" aria-hidden="true"></i>
          <strong>产品暂时无法加载</strong>
          <p>请稍后重试，接口恢复后已上架产品将在此展示。</p>
          <NiceButton type="button" @click="retry">重新加载</NiceButton>
        </div>

        <template v-else-if="products.length > 0">
          <div class="product-grid" role="list">
            <product-card
              v-for="product in products"
              :key="product.id"
              :product="product"
              role="listitem"
            />
          </div>

          <div
            ref="paginationReveal"
            class="product-pagination"
            :class="{
              'is-reveal-ready': paginationRevealReady,
              'is-revealed': paginationRevealed,
            }"
          >
            <site-pagination
              :page="page"
              :page-count="pageCount"
              :pending="pending"
              label="产品分页"
              @update:page="handlePageChange"
            />
          </div>
        </template>

        <div v-else class="product-state is-empty" role="status">
          <i class="i-lucide-package-search" aria-hidden="true"></i>
          <strong>当前分类下暂无产品</strong>
          <p>请切换其他分类后重试。</p>
          <NiceButton type="button" @click="resetFilters">重置筛选</NiceButton>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { ApiData, PageResponse, ProductCategory, ProductItem } from '~/types/site-api'

  import { NiceButton } from '@nice/nice-ui'

  import { getRouteScrollNavigationId, isScrollRestorationNavigation } from '~/utils/route-scroll'

  defineOptions({
    name: 'ProductBrowser',
  })

  const route = useRoute()
  const { getProductCategories, getProducts } = useSiteApi()
  const browserSection = useTemplateRef<HTMLElement>('browserSection')
  const categoryNav = useTemplateRef<HTMLElement>('categoryNav')
  const routeScrollInstanceId = getRouteScrollNavigationId()
  const page = shallowRef(1)
  const selectedCategoryCode = shallowRef('')
  const isBrowserMounted = shallowRef(false)
  const filterScrollRequest = shallowRef(0)
  const filterScrollTarget = shallowRef<'page' | 'section'>('section')
  const loadedQueryKey = shallowRef('')
  const hasResolvedCategory = shallowRef(false)
  let filterScrollSequence = 0
  /** 当前激活分类编码，直接取路由，避免依赖异步数据造成高亮闪烁。 */
  const activeCategoryCode = computed(() => queryValue(route.query.category))

  const {
    targetRef: paginationReveal,
    isReady: paginationRevealReady,
    isVisible: paginationRevealed,
  } = useScrollReveal(0.1)

  const {
    data: categoryResponse,
    pending: categoriesPending,
    error: categoryError,
    refresh: refreshCategories,
  } = await useAsyncData<ApiData<ProductCategory[]>>(
    'site-product-categories',
    getProductCategories
  )

  const categories = computed(() => categoryResponse.value?.data ?? [])

  /** 将 route query 的多值类型收敛为单个字符串。 */
  function queryValue(value: (null | string)[] | null | string | undefined) {
    return Array.isArray(value)
      ? (value.find((item): item is string => item !== null) ?? '')
      : (value ?? '')
  }

  /** 将稳定 URL 分类编码映射到当前环境的接口编码。 */
  function resolveCategoryCode(value: string) {
    return (
      categories.value.find((category) => category.code === value || category.name === value)
        ?.code ?? ''
    )
  }

  watch(
    [categories, () => route.query.category],
    ([, value]) => {
      const nextCategoryCode = resolveCategoryCode(queryValue(value))
      if (nextCategoryCode === selectedCategoryCode.value) {
        if (nextCategoryCode) hasResolvedCategory.value = true
        return
      }
      const shouldScroll =
        isBrowserMounted.value && hasResolvedCategory.value && !isScrollRestorationNavigation()
      loadedQueryKey.value = ''
      selectedCategoryCode.value = nextCategoryCode
      page.value = 1
      if (shouldScroll) {
        filterScrollTarget.value = 'page'
        filterScrollRequest.value = ++filterScrollSequence
      }
      if (nextCategoryCode) hasResolvedCategory.value = true
    },
    { immediate: true }
  )

  const {
    data: productResponse,
    pending: productsPending,
    error: productError,
    status: productStatus,
    refresh: refreshProducts,
  } = await useAsyncData<PageResponse<ProductItem>>(
    'product-browser-items',
    async (_nuxtApp, { signal }) => {
      const queryKey = `${selectedCategoryCode.value}:${page.value}`
      loadedQueryKey.value = ''
      try {
        const response = await getProducts(
          {
            category: selectedCategoryCode.value || undefined,
            page: page.value,
            page_limit: 12,
          },
          signal
        )
        if (!signal.aborted) loadedQueryKey.value = queryKey
        return response
      } catch (error) {
        if (!signal.aborted) loadedQueryKey.value = queryKey
        throw error
      }
    },
    { watch: [selectedCategoryCode, page] }
  )

  if (
    import.meta.client &&
    productResponse.value !== undefined &&
    (productStatus.value === 'success' || productStatus.value === 'error')
  ) {
    loadedQueryKey.value = `${selectedCategoryCode.value}:${page.value}`
  }

  const currentQueryKey = computed(() => `${selectedCategoryCode.value}:${page.value}`)
  const hasLoadedCurrentQuery = computed(() => loadedQueryKey.value === currentQueryKey.value)
  const showListLoading = computed(() => !categoryError.value && !hasLoadedCurrentQuery.value)
  const products = computed(() =>
    hasLoadedCurrentQuery.value ? (productResponse.value?.data ?? []) : []
  )
  const pageCount = computed(() => productResponse.value?.meta.last_page ?? 1)
  const pending = computed(() => categoriesPending.value || productsPending.value)
  const hasError = computed(() => Boolean(categoryError.value || productError.value))
  const isRouteScrollReady = computed(
    () =>
      !pending.value &&
      loadedQueryKey.value === `${selectedCategoryCode.value}:${page.value}` &&
      (productStatus.value === 'success' || productStatus.value === 'error')
  )

  /** 将列表起点带回视口。 */
  function scrollToBrowserSection(behavior?: ScrollBehavior) {
    if (!import.meta.client) return
    browserSection.value?.scrollIntoView({
      behavior:
        behavior ??
        (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'),
      block: 'start',
    })
  }

  /** 将分类切换后的页面回到原有的顶部位置。 */
  function scrollToPageTop() {
    if (!import.meta.client) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      left: 0,
      top: 0,
      behavior: reduceMotion ? 'auto' : 'smooth',
    })
  }

  watch(
    [productsPending, products],
    async ([isPending], _oldValue, onCleanup) => {
      const requestId = filterScrollRequest.value
      if (!requestId || isPending) return
      const scrollTarget = filterScrollTarget.value

      let cancelled = false
      onCleanup(() => {
        cancelled = true
      })

      await nextTick()
      if (
        cancelled ||
        requestId !== filterScrollRequest.value ||
        productsPending.value ||
        !isBrowserMounted.value
      )
        return

      filterScrollRequest.value = 0
      if (scrollTarget === 'page') scrollToPageTop()
      else scrollToBrowserSection()
    },
    { flush: 'post' }
  )

  /** 将当前产品筛选状态同步到 URL。 */
  function syncFilterQuery() {
    const query = { ...route.query }
    if (selectedCategoryCode.value) query.category = selectedCategoryCode.value
    else delete query.category
    void navigateTo({ path: route.path, query }, { replace: true })
  }

  /** 切换产品分类。 */
  function changeCategory(categoryCode: string) {
    if (categoryCode === selectedCategoryCode.value && page.value === 1) return
    if (isBrowserMounted.value) {
      filterScrollTarget.value = 'page'
      filterScrollRequest.value = ++filterScrollSequence
    }
    hasResolvedCategory.value = true
    loadedQueryKey.value = ''
    selectedCategoryCode.value = categoryCode
    page.value = 1
    syncFilterQuery()
  }

  /** 重置产品分类筛选。 */
  function resetFilters() {
    if (!selectedCategoryCode.value && page.value === 1) return
    if (isBrowserMounted.value) {
      filterScrollTarget.value = 'page'
      filterScrollRequest.value = ++filterScrollSequence
    }
    hasResolvedCategory.value = true
    loadedQueryKey.value = ''
    selectedCategoryCode.value = ''
    page.value = 1
    syncFilterQuery()
  }

  /** 重新加载分类与产品数据。 */
  async function retry() {
    await refreshCategories()
    await refreshProducts()
  }

  /** 切换产品分页并回到列表起点。 */
  function handlePageChange(nextPage: number) {
    if (isBrowserMounted.value) {
      // 旧列表仍保持完整高度时先定位，避免 loading 替换网格后视口随页面坍缩。
      scrollToBrowserSection('auto')
      filterScrollTarget.value = 'section'
      filterScrollRequest.value = ++filterScrollSequence
    }
    loadedQueryKey.value = ''
    page.value = nextPage
  }

  /** 将当前激活的 Tab 滚动到可视区域居中位置。 */
  async function positionSelectedCategory() {
    if (!import.meta.client) return
    await nextTick()

    const nav = categoryNav.value
    const activeItem = nav?.querySelector<HTMLElement>('.product-tab-item.is-active')
    if (!nav || !activeItem) return

    const navRect = nav.getBoundingClientRect()
    const activeRect = activeItem.getBoundingClientRect()
    const centeredOffset = (navRect.width - activeRect.width) / 2
    nav.scrollTo({
      left: Math.max(0, nav.scrollLeft + activeRect.left - navRect.left - centeredOffset),
      behavior: 'auto',
    })
    positionTabIndicator()
  }

  /** 将激活态下划线滑动到当前 Tab 位置。 */
  function positionTabIndicator() {
    if (!import.meta.client) return
    const nav = categoryNav.value
    const indicator = nav?.querySelector<HTMLElement>('.product-tab-indicator')
    const activeItem = nav?.querySelector<HTMLElement>('.product-tab-item.is-active')
    if (!nav || !indicator) return
    if (!activeItem) {
      indicator.style.width = '0'
      indicator.style.transform = ''
      return
    }
    const targetWidth = `${activeItem.offsetWidth}px`
    const targetTransform = `translateX(${activeItem.offsetLeft}px)`
    // 首次定位（无历史 transform）时禁用过渡，直接到位，避免从页面左侧滑入；后续切换正常滑动转移。
    if (!indicator.style.transform) {
      indicator.style.transition = 'none'
      indicator.style.width = targetWidth
      indicator.style.transform = targetTransform
      void indicator.offsetWidth
      indicator.style.transition = ''
      return
    }
    indicator.style.width = targetWidth
    indicator.style.transform = targetTransform
  }

  watch(activeCategoryCode, positionSelectedCategory, { flush: 'post' })
  watch(categories, positionTabIndicator, { flush: 'post' })

  onMounted(() => {
    isBrowserMounted.value = true
    if (!categoriesPending.value) hasResolvedCategory.value = true
    positionSelectedCategory()
    window.addEventListener('resize', positionTabIndicator)
  })

  onUnmounted(() => {
    isBrowserMounted.value = false
    window.removeEventListener('resize', positionTabIndicator)
  })
</script>

<style scoped lang="scss">
  @keyframes product-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (hover: hover) and (pointer: fine) {
    .product-tab-item:hover {
      color: var(--accent);
    }

    .product-state button:hover {
      background: var(--primary-strong);
      border-color: var(--primary-strong);
    }
  }

  @media (hover: none) {
    .product-tab-item:active {
      color: var(--accent);
    }

    .product-state button:active {
      background: var(--primary-strong);
      border-color: var(--primary-strong);
    }
  }

  .product-browser-section {
    position: relative;
    z-index: 2;
    padding-top: 0;
    scroll-margin-top: 92px;
    background: var(--surface-soft);

    @include lt-sm {
      padding: 0 0 48px;
      scroll-margin-top: 72px;
    }
  }

  /* 通栏 Tab 栏 */
  .product-browser-tabbar {
    background: var(--surface);
    border-bottom: 1px solid var(--line);
  }

  .product-tab-nav {
    position: relative;
    display: flex;
    gap: 30px;
    justify-content: safe center;
    min-width: 0;
    padding-top: 16px;
    overflow-x: auto;
    overscroll-behavior-inline: contain;
    scroll-snap-type: inline proximity;
    scrollbar-width: none;
    border-bottom: 1px solid var(--line);

    @include lt-sm {
      gap: 22px;
      padding-top: 12px;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .product-tab-item {
    position: relative;
    flex-shrink: 0;
    padding: 0 2px 16px;
    font-size: 14px;
    line-height: 1.4;
    color: var(--muted);
    white-space: nowrap;
    cursor: pointer;
    scroll-snap-align: start;
    transition: color 0.2s ease;

    @include lt-sm {
      padding-bottom: 12px;
      font-size: 13px;
    }
  }

  .product-tab-item.is-active {
    font-weight: 600;
    color: var(--accent);
  }

  .product-tab-indicator {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 3px;
    background: var(--accent);
    transition:
      transform 280ms var(--ease),
      width 280ms var(--ease);
  }

  .product-state button {
    min-height: 46px;
    padding: 9px 18px;
    margin-top: 8px;
    font-weight: 600;
    color: var(--white);
    cursor: pointer;
    background: var(--accent);
    border: 1px solid var(--accent);
    border-radius: var(--radius-md);
  }

  .product-state button:hover {
    background: var(--primary-strong);
    border-color: var(--primary-strong);
  }

  /* 产品列表区 */
  .product-browser-content {
    min-width: 0;
    padding-top: 24px;
    padding-bottom: clamp(40px, 4vw, 56px);

    @include lt-sm {
      padding-top: 20px;
      padding-bottom: 8px;
    }
  }

  .product-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px;
    align-items: start;

    @include lt-sm {
      grid-template-columns: minmax(0, 1fr);
      gap: 14px;
    }
  }

  .product-pagination.is-reveal-ready:not(.is-revealed) {
    opacity: 0;
    transform: translateY(24px);

    @media (prefers-reduced-motion: reduce) {
      opacity: 1;
      transform: none;
    }
  }

  .product-pagination.is-reveal-ready.is-revealed {
    opacity: 1;
    transform: none;
    transition:
      opacity 520ms var(--ease),
      transform 520ms var(--ease);
  }

  .product-state {
    display: grid;
    gap: 10px;
    place-content: center;
    justify-items: center;
    min-height: 280px;
    padding: 40px 24px;
    color: var(--muted);
    text-align: center;
    background: var(--surface);
    border: 1px dashed var(--line);
    border-radius: var(--radius-md);

    @include lt-sm {
      min-height: 220px;
      padding: 32px 20px;
    }
  }

  .product-state > i {
    width: 34px;
    height: 34px;
    color: var(--accent);
  }

  .product-state strong {
    font-size: 18px;
    color: var(--ink);
  }

  .product-state p {
    margin: 0;
  }

  .is-spinning {
    animation: product-spin 900ms linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
</style>
